import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getActiveProcessesInfo } from '../../transactions/transaction';
import { storableError } from '../../util/errors';

const DASHBOARD_TRANSACTION_PAGE_SIZE = 100;

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const transactionQueryParams = only => ({
  only,
  processNames: getActiveProcessesInfo().map(process => process.name),
  include: ['listing', 'provider', 'provider.profileImage', 'customer', 'customer.profileImage'],
  'fields.transaction': ['processName', 'lastTransition', 'lastTransitionedAt', 'transitions'],
  'fields.listing': ['title', 'deleted', 'state'],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
  sort: 'lastTransitionedAt',
  page: 1,
  perPage: DASHBOARD_TRANSACTION_PAGE_SIZE,
});

const loadDataPayloadCreator = async (_, { dispatch, extra: sdk, rejectWithValue }) => {
  try {
    const [orders, sales] = await Promise.all([
      sdk.transactions.query(transactionQueryParams('order')),
      sdk.transactions.query(transactionQueryParams('sale')),
    ]);

    dispatch(addMarketplaceEntities(orders));
    dispatch(addMarketplaceEntities(sales));

    const orderTransactions = orders?.data?.data || [];
    const saleTransactions = sales?.data?.data || [];

    return {
      orderRefs: entityRefs(orderTransactions),
      saleRefs: entityRefs(saleTransactions),
    };
  } catch (error) {
    return rejectWithValue(storableError(error));
  }
};

export const loadDataThunk = createAsyncThunk('UserDashboardPage/loadData', loadDataPayloadCreator);

const userDashboardPageSlice = createSlice({
  name: 'UserDashboardPage',
  initialState: {
    fetchInProgress: false,
    fetchDashboardError: null,
    orderRefs: [],
    saleRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchDashboardError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.orderRefs = action.payload.orderRefs;
        state.saleRefs = action.payload.saleRefs;
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.fetchInProgress = false;
        state.fetchDashboardError = action.payload;
      });
  },
});

export default userDashboardPageSlice.reducer;

export const loadData = () => dispatch => dispatch(loadDataThunk());
